import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisH,
  faGripVertical,
  faRedo,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTiktok,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { SocialWallPost, SocialWallStream } from "../../../cores/socialWall/interfaces";
import { socialWallApiService } from "../../../cores/socialWall/services/api";
import { PROVIDER_META } from "./boardStorage";
import { SocialWallPostCard } from "./SocialWallPostCard";

const ICONS: Record<string, typeof faFacebook> = {
  facebook: faFacebook,
  instagram: faInstagram,
  tiktok: faTiktok,
  linkedin_business: faLinkedin,
  youtube: faYoutube,
};

interface Props {
  companyId: string;
  stream: SocialWallStream;
  compact?: boolean;
  filterQ?: string;
  mediaType?: string;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  onRemove: (streamId: string) => void;
  onToggleMinimize: (streamId: string) => void;
  onMove: (streamId: string, direction: "left" | "right") => void;
  onDragStart: (streamId: string) => void;
  onDragOver: (streamId: string) => void;
  onDragEnd: () => void;
  isDragging?: boolean;
  isDropTarget?: boolean;
}

export function SocialWallColumn({
  companyId,
  stream,
  compact,
  filterQ,
  mediaType,
  canMoveLeft,
  canMoveRight,
  onRemove,
  onToggleMinimize,
  onMove,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging,
  isDropTarget,
}: Props) {
  const intl = useIntl();
  const [posts, setPosts] = useState<SocialWallPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const allowDragRef = useRef(false);

  const meta = PROVIDER_META[stream.provider] || {
    label: stream.provider,
    color: "#64748b",
  };
  const icon = ICONS[stream.provider] || faFacebook;
  const isExternal = stream.streamType === "external";
  const minimized = Boolean(stream.minimized);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await socialWallApiService.getFeed(companyId, {
        provider: stream.provider,
        limit: 25,
        q: filterQ || undefined,
        mediaType: mediaType && mediaType !== "all" ? mediaType : undefined,
        streamType: isExternal ? "external" : "my_posts",
        externalId: isExternal ? stream.externalId : undefined,
        externalUsername: isExternal
          ? stream.externalUsername || undefined
          : undefined,
      });
      setPosts(payload.posts || []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ||
        (e as { message?: string })?.message ||
        "No se pudieron cargar las publicaciones";
      setError(String(msg));
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    stream.provider,
    stream.externalId,
    stream.externalUsername,
    isExternal,
    filterQ,
    mediaType,
  ]);

  useEffect(() => {
    if (minimized) return;
    refresh();
  }, [refresh, minimized]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const columnClass = [
    "sw-column",
    isExternal ? "sw-column--external" : "",
    minimized ? "sw-column--minimized" : "",
    isDragging ? "sw-column--dragging" : "",
    isDropTarget ? "sw-column--drop-target" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={columnClass}
      draggable
      onDragStart={(e) => {
        if (!allowDragRef.current) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", stream.id);
        onDragStart(stream.id);
        setMenuOpen(false);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver(stream.id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        allowDragRef.current = false;
        onDragEnd();
      }}
      onDragEnd={() => {
        allowDragRef.current = false;
        onDragEnd();
      }}
    >
      <header
        className="sw-column__header"
        style={{ borderTopColor: meta.color }}
        onDoubleClick={() => onToggleMinimize(stream.id)}
        onClick={
          minimized
            ? (e) => {
                const target = e.target as HTMLElement;
                if (
                  target.closest(".sw-column__actions") ||
                  target.closest(".sw-column__drag") ||
                  target.closest(".sw-column-menu")
                ) {
                  return;
                }
                onToggleMinimize(stream.id);
              }
            : undefined
        }
      >
        <button
          type="button"
          className="sw-column__drag"
          title={intl.formatMessage({ id: "app.socialWall.dragColumn" })}
          aria-label={intl.formatMessage({ id: "app.socialWall.dragColumn" })}
          onMouseDown={() => {
            allowDragRef.current = true;
          }}
          onTouchStart={() => {
            allowDragRef.current = true;
          }}
        >
          <FontAwesomeIcon icon={faGripVertical} />
        </button>
        <div className="sw-column__title">
          <FontAwesomeIcon icon={icon} style={{ color: meta.color }} />
          <div>
            <strong>{stream.title}</strong>
            <small>
              {stream.accountName || meta.label}
              {isExternal
                ? ` · ${intl.formatMessage({
                    id: "app.socialWall.competitorsBadge",
                  })}`
                : ""}
            </small>
          </div>
        </div>
        <div className="sw-column__actions" ref={menuRef}>
          {!minimized ? (
            <button
              type="button"
              className="sw-icon-btn"
              onClick={refresh}
              title={intl.formatMessage({ id: "app.socialWall.refreshColumn" })}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faRedo} spin={loading} />
            </button>
          ) : null}
          <button
            type="button"
            className={`sw-icon-btn${menuOpen ? " sw-icon-btn--active" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
            title={intl.formatMessage({ id: "app.socialWall.columnOptions" })}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
          {menuOpen ? (
            <div className="sw-column-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onToggleMinimize(stream.id);
                  setMenuOpen(false);
                }}
              >
                {minimized
                  ? intl.formatMessage({ id: "app.socialWall.expandColumn" })
                  : intl.formatMessage({ id: "app.socialWall.minimizeColumn" })}
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!canMoveLeft}
                onClick={() => {
                  onMove(stream.id, "left");
                  setMenuOpen(false);
                }}
              >
                {intl.formatMessage({ id: "app.socialWall.moveLeft" })}
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!canMoveRight}
                onClick={() => {
                  onMove(stream.id, "right");
                  setMenuOpen(false);
                }}
              >
                {intl.formatMessage({ id: "app.socialWall.moveRight" })}
              </button>
              {!minimized ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    void refresh();
                    setMenuOpen(false);
                  }}
                >
                  {intl.formatMessage({ id: "app.socialWall.refreshColumn" })}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className="sw-column-menu__danger"
                onClick={() => {
                  onRemove(stream.id);
                  setMenuOpen(false);
                }}
              >
                {intl.formatMessage({ id: "app.socialWall.removeColumn" })}
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className="sw-icon-btn"
            onClick={() => onRemove(stream.id)}
            title={intl.formatMessage({ id: "app.socialWall.removeColumn" })}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </header>

      {!minimized ? (
        <div className="sw-column__body">
          {loading && !posts.length ? (
            <div className="sw-empty">
              {intl.formatMessage({ id: "app.socialWall.loadingPosts" })}
            </div>
          ) : null}
          {error ? <div className="sw-error">{error}</div> : null}
          {!loading && !error && !posts.length ? (
            <div className="sw-empty">
              {intl.formatMessage({ id: "app.socialWall.emptyColumn" })}
            </div>
          ) : null}
          {posts.map((post) => (
            <SocialWallPostCard
              key={post.id}
              post={post}
              companyId={companyId}
              compact={compact}
              onDeleted={(postId) =>
                setPosts((prev) => prev.filter((p) => p.id !== postId))
              }
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
